import produce from 'immer';
import {Spec} from 'immutability-helper';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, ANALYTICS_MODULE, ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ClientId} from 'notebook/ELNConstants';
import {StoichiometryStructuredTable} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {ParameterStructuredTable} from 'notebook/integrations/procedures/ParameterStructuredTableAdapter';
import {autofixParameterTableSchemaMetadatas} from 'notebook/integrations/procedures/ProceduresUtil';
import {RegistrationStructuredTable} from 'notebook/integrations/registry/RegistrationStructuredTableAdapter';
import {ResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {BoxCreationStructuredTable} from 'notebook/integrations/samples/BoxCreationStructuredTableAdapter';
import {InventoryStructuredTable} from 'notebook/integrations/samples/InventoryStructuredTableAdapter';
import {PlateCreationStructuredTable} from 'notebook/integrations/samples/PlateCreationStructuredTableAdapter';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {MediocreItemAction, StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import {
  buildTableSearchResult,
  doesTableCellMatch,
  getSearchResultsForColumnNames,
  getSearchResultsForTableName,
} from 'notebook/mediocre/search/TableSearchUtil';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import autofixStructuredTableSingleInputs from 'notebook/structured-tables/single-inputs/util/autofixStructuredTableSingleInputs';
import checkStructuredTableSingleInputsValid from 'notebook/structured-tables/single-inputs/util/checkStructuredTableSingleInputsValid';
import {
  BaseTagValueStructuredTableColumn,
  LastSyncedUpstreamValue,
  StructuredTable,
  StructuredTableAdapter,
  StructuredTableKind,
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import buildRowUiData from 'notebook/structured-tables/util/buildRowUiData';
import {doesNoteIncludeStructuredTableUuid} from 'notebook/structured-tables/util/getAllStructuredTablesInNote';
import {getLiveRowMetadataFromRowUpdater} from 'notebook/structured-tables/util/getLiveRowMetadataFromRowUpdater';
import {CollapsedTableState} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {isTableCollapsed} from 'notebook/tables/collapse/useTableCollapse';
import {CELL_UI_PROPERTIES} from 'notebook/tables/constants/CellConstants';
import {RowDataUiData} from 'notebook/tables/constants/RowConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import TableSkeleton, {getTableSkeletonHeight} from 'notebook/tables/core-ui/TableSkeleton';
import {EvaluatedCell, OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {toStringTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {
  AnyStructuredTable,
  NoteLinkedObjectType,
  NotePart,
  NotePartWithItem,
} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import APIUtil from 'util/APIUtil';
import {omit} from 'util/omitKey';

import {getStructuredTableAdapters, isKnownStructuredTableAdapter} from './StructuredTableAdapters';
import StructuredTableFormOrView from './StructuredTableFormOrView';
import {getMaxRowsConfig} from './util/checkTableCapacity';
import {doesUserHavePermissionToViewTable} from './util/doesUserHavePermissionToViewTable';

interface NoteLinkedStructuredTableContent {
  item?: NoteLinkedStructuredTable;
  table?: Table;
  lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>;
}

// TODO(BNCH-123450): This union should likely be changed in favor of a more general StructuredTable union
// to allow removing the as casts
export type NoteLinkedStructuredTable =
  | RegistrationStructuredTable
  | ResultsTable
  | InventoryStructuredTable
  | ParameterStructuredTable
  | PlateCreationStructuredTable
  | BoxCreationStructuredTable
  | StoichiometryStructuredTable;

/**
 * A structured table NoteEditorPlugin which handles the base case for
 * our structured tables.  Additional functionality can be layered on
 * top.
 */
export const STRUCTURED_TABLE_PLUGIN: NoteEditorPlugin & {
  get: (kind: string) => UnknownStructuredTableAdapter;
} = {
  type: NoteLinkedObjectType.STRUCTURED_TABLE,
  typeClass: 'is-table',
  title: 'Structured Table',
  icon: 'note-table',

  // A helper function to get the particular StructuredTableAdapter for a given kind.
  get: (kind: StructuredTableKind) => {
    if (isKnownStructuredTableAdapter(kind)) {
      return getStructuredTableAdapters()[kind];
    } else {
      throw new Error(`Unknown structured table kind: ${kind}`);
    }
  },

  // Our component maker which returns our StructuredTableView
  component: {
    selectionInit: MediocrePlugins.table.selectionInit,
    selectionAtStart: MediocrePlugins.table.selectionAtStart,
    selectionAtEnd: MediocrePlugins.table.selectionAtEnd,
    required: [],
    getItemProps(item: NotePart, index: number): TableItemProps {
      return MediocrePlugins.getTableItemProps.call(this, item, index);
    },

    getIsCollapsed(
      item: NotePart & {item: NoteLinkedStructuredTable},
      editorProps: NoteEditorProps,
      collapsedStateById: CollapsedTableState
    ) {
      const {rowCount, cellCount} = TableUtil.getTableDimensionsFromStructuredTable(item.item);
      return isTableCollapsed({
        collapseId: item.item.provenanceUuid,
        collapsedStateById,
        rowCount,
        cellCount,
        tableKind: item.item.kind,
        autoCollapseLimit: BenchlingConfig.ELN_ROW_LIMIT_FOR_AUTO_COLLAPSE,
      });
    },

    getIsInaccessible(item: NotePart & {item: NoteLinkedStructuredTable}): boolean {
      return !doesUserHavePermissionToViewTable(item.item);
    },

    getVirtualizedHeight(notePart: NotePart & {item: NoteLinkedStructuredTable}, isCollapsed: boolean) {
      return getTableSkeletonHeight(notePart.item.rows.length, isCollapsed);
    },

    renderVirtualizedSkeleton(
      notePart: NotePart & {item: NoteLinkedStructuredTable},
      height: number
    ): JSX.Element {
      return <TableSkeleton height={height} />;
    },

    getVirtualizationId(this: NoteEditor, notePart: NotePart & {item: NoteLinkedStructuredTable}): string {
      return notePart.item.provenanceUuid;
    },

    make(
      // Plugin methods are always called using Function.prototype.call,
      // so `this` refers to the editor component.
      this: NoteEditor,
      notePart: NotePart & {item: NoteLinkedStructuredTable},
      index: number,
      editorProps: NoteEditorProps,
      itemProps: TableItemProps & {forceLiveness?: number},
      searchResultProps: MediocreSearchResultProps
    ): JSX.Element {
      const adapter = getStructuredTableAdapters()[notePart.item.kind];
      assert(adapter != null, `Unknown structured table kind: ${notePart.item.kind}`);
      return (
        <StructuredTableFormOrView
          editor={this}
          notePart={notePart}
          editorProps={editorProps}
          itemProps={itemProps}
          index={index}
          adapter={adapter}
          {...searchResultProps}
        />
      );
    },
  },

  canPasteFromOtherTenant: false,

  // Plugin method are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemFromHTML<TTableMetadata extends {}, TRowMetadata extends {}>(
    this: NoteEditor,
    $el: JQuery,
    item: NotePartWithItem<StructuredTable<TTableMetadata, TRowMetadata>>
  ): NotePart | null {
    const structuredTableAdapter = getStructuredTableAdapters()[item.item.kind] as StructuredTableAdapter<
      TTableMetadata,
      TRowMetadata
    >;

    if (EntityStore.currentUserId !== $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_USER_ID)) {
      notifications.warn('Cannot paste tables across user accounts.');
      return null;
    }

    if (!structuredTableAdapter.allowPasteTable()) {
      notifications.warn(`Cannot paste "${structuredTableAdapter.getDisplayName(item.item)}" tables.`, {
        // id ensures we de-duplicate warnings when trying to paste the same table
        id: `cannot-paste-${item.item.provenanceUuid}`,
      });
      return null;
    }

    const sourceFileId = $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID);
    const destinationFileId = this.props.fileId;

    // Helper function to determine action origin based on destination file
    const getActionOrigin = (fileId: string): ActionOrigin => {
      if (APIUtil.isDocument(fileId)) {
        return ActionOrigin.DOCUMENT;
      }

      if (APIUtil.apiIdToType(fileId) === 'mixture_instructions' || APIUtil.isBiologicalEntity(fileId)) {
        return ActionOrigin.REGISTRY_ITEM_PAGE;
      }

      return ActionOrigin.NOTEBOOK;
    };

    const wantsConfig = this.props.isConfig || APIUtil.isEntryOrSubOrWorkflowTemplate(destinationFileId);
    if (!wantsConfig && !APIUtil.isBaseEntry(destinationFileId)) {
      // Track blocked paste attempt
      analytics.trackEventWithModule(
        ELN_ANALYTICS_ACTIONS.BLOCKED_PASTE_NOTEBOOK_ITEM,
        ANALYTICS_MODULE.ELN,
        {
          actionOrigin: getActionOrigin(destinationFileId),
          pasted_item_type: `structured_table_${item.item.kind.toLowerCase()}`,
          destination_file_type: APIUtil.apiIdToType(destinationFileId),
          source_file_api_id: sourceFileId,
          destination_file_api_id: destinationFileId,
        }
      );

      notifications.warn(
        `Cannot paste "${structuredTableAdapter.getDisplayName(item.item)}" tables outside Notebook.`,
        {id: `cannot-paste-${item.item.provenanceUuid}`}
      );
      return null;
    }

    // NB: we do not need to worry about name / provenanceUuid / rowId collisions as that is handled by
    // client/notebook/mediocre/core-ui/structured-tables/convertStructuredTableForPaste.ts

    // Track successful paste (restrictions passed)
    analytics.trackEventWithModule(ELN_ANALYTICS_ACTIONS.PASTED_NOTEBOOK_ITEM, ANALYTICS_MODULE.ELN, {
      actionOrigin: getActionOrigin(destinationFileId),
      pasted_item_type: `structured_table_${item.item.kind.toLowerCase()}`,
      destination_file_type: APIUtil.apiIdToType(destinationFileId),
      source_file_api_id: sourceFileId,
      destination_file_api_id: destinationFileId,
    });

    if (wantsConfig) {
      if (item.item.isConfig) {
        return item;
      } else if (item.item.rows.some((row) => row.isFinalized)) {
        // TODO(BNCH-65974): Remove this block and instead strip all finalized data from rows.
        const displayName = structuredTableAdapter.getDisplayName(item.item);
        const message = `Preserving cell data on paste into templates for submitted "${displayName}" tables is not currently supported.`;
        notifications.warn(message, {id: message}); // id ensures we de-duplicate on paste of multiple tables
        return produce(item, (draft) => {
          const draftTable = draft.item as StructuredTable<TTableMetadata, TRowMetadata>;
          draftTable.isConfig = true;
          const blankRow = structuredTableAdapter.getBlankRow(item.item.metadata, 0, item.item.columns);
          draftTable.rows = [blankRow];
        });
      } else {
        return produce(item, (draft) => {
          let draftTable = draft.item as StructuredTable<TTableMetadata, TRowMetadata>;
          draftTable.isConfig = true;
          draftTable = structuredTableAdapter.fromTable(structuredTableAdapter.toTable(draftTable));
          (draft as {item: StructuredTable<TTableMetadata, TRowMetadata>}).item = draftTable;
        });
      }
    }
    // !wantsConfig
    if (item.item.isConfig) {
      // Copy-and-paste for a config is treated the same as if we were to create the entry from template,
      // which is currently as simple as flipping the isConfig flag. Keep in sync with
      // benchling/notebook/structured_tables/models/structured_table.py:StructuredTable.clone

      return produce(item, (draft) => {
        const draftTable = draft.item as StructuredTable<TTableMetadata, TRowMetadata>;
        draftTable.isConfig = false;
      });
    } else {
      // Allow copy and pasting into an entry as long as we're either cut-and-pasting within the same entry
      // or the table has no finalized data.
      const isBeingCutAndPasteWithinSameEntry =
        sourceFileId === destinationFileId &&
        !doesNoteIncludeStructuredTableUuid(this.props.note, item.item.provenanceUuid);
      if (!isBeingCutAndPasteWithinSameEntry && item.item.rows.some((row) => row.isFinalized)) {
        const displayName = structuredTableAdapter.getDisplayName(item.item);
        const message = `Preserving cell data on paste for submitted "${displayName}" tables is not currently supported.`;
        notifications.warn(message, {id: message}); // id ensures we de-duplicate on paste of multiple tables
        return produce(item, (draft) => {
          const draftTable = draft.item as StructuredTable<TTableMetadata, TRowMetadata>;
          // TODO(BNCH-92835): handle parameter+ tables, which have delegate columns
          draftTable.rows = [structuredTableAdapter.getBlankRow(item.item.metadata, 0, item.item.columns)];

          // Clear importSources metadata since the imported rows are no longer present
          if (draftTable.metadata?.importSources) {
            delete draftTable.metadata.importSources;
          }
        });
      } else {
        return item;
      }
    }
  },

  // Plugin method are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemToHTML<TTableMetadata, TRowMetadata>(
    this: NoteEditor,
    $el: JQuery,
    item: NotePart & {item: StructuredTable<TTableMetadata, TRowMetadata>}
  ): JQuery {
    $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID, this.props.fileId);
    $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_USER_ID, EntityStore.currentUserId);

    const fullTableMap = this.props.makeFullTableMap(this.props);
    const fullTable = fullTableMap[item.item.name];

    const computedTables = this.props.computedTables;
    if (!computedTables.hasOwnProperty(item.item.name)) {
      return $(
        $.parseHTML(
          TableUtil.tableEditableToText(
            fullTable,
            fullTable.table.map((row) =>
              row.map(() => ({value: toStringTypedValue(''), locked: false}) as EvaluatedCell)
            )
          )
        )
      );
    }

    const computedTable = computedTables[item.item.name];
    $el.append(TableUtil.tableEditableToHTML(fullTable, computedTable));
    return $el;
  },
  // This must stay in sync with table.name returned from getContent
  getSpreadsheetTableName({item}: NotePart & {item: NoteLinkedStructuredTable}): string {
    return item.name;
  },
  getContent(item: NotePart & {item: NoteLinkedStructuredTable}): {table: Table} {
    if (!isKnownStructuredTableAdapter(item.item.kind)) {
      throw new Error(`Unknown structured table kind: ${item.item.kind}`);
    }

    const structuredTableAdapter: UnknownStructuredTableAdapter =
      getStructuredTableAdapters()[item.item.kind];
    return {table: structuredTableAdapter.toTable(item.item)};
  },

  /**
   * This method is called either during insertion of the new item into the NoteEditor or it's
   * called from the NoteEditorContainer as part of the `updateHandler` call chain from the
   * rendering component of the plugin.
   *
   * When called from the NoteEditor after insertion, we will receive an 'item' and we can just pass
   * it along the call chain where it will ultimately be persisted into the note's store and sent to
   * the server.
   *
   * When called from the NoteEditorContatiner as part of an edit, we will receive a 'Table' and we
   * will need to convert the 'Table' into a 'StructuredTable' before continuing on the call chain
   * to where it will be persisted in the store.  Additionally, if the
   * 'lastSyncedUpstreamValuesByRow' field is provided, we will update the given rows' lastSyncedUpstreamValue
   * with the provided value.  This allows us to know when a field is out of date from the content in the
   * store or when we've modified the cell.
   *
   * @param item - a StructuredTable we should persist in the note store
   * @param table - a Table that we should convert to a StructuredTable before persisting in the
   *                store
   * @param lastSyncedUpstreamValuesByRow - if present, we should update the StructuredTable's
   *                                        lastSyncedValues before persisting in the store
   */
  setContent(content: NoteLinkedStructuredTableContent): Spec<NotePart> {
    return setStructuredTableContent(content);
  },

  getStoreDependenciesForSpreadsheetEval(
    item: NotePart & {item: NoteLinkedStructuredTable}
  ): StoreDependencyMap {
    const adapter = getStructuredTableAdapters()[item.item.kind];
    const primarySamplesByRowId = adapter.getPrimarySamples(item.item);
    const primarySampleLookupIds = Object.values(primarySamplesByRowId).flatMap((primarySamples) =>
      primarySamples.flatMap((primarySample) => primarySample.sampleLookupInfo?.idForLookup ?? [])
    );
    const bioentityIds = primarySampleLookupIds.filter((sampleId) => APIUtil.isBiologicalEntity(sampleId));
    const storableIds = primarySampleLookupIds.filter((sampleId) => APIUtil.isStorable(sampleId));
    return {
      bioentityIds,
      storableIds,
    };
  },

  getReasonForDisallowedAction(
    this: NoteEditor,
    item: NotePart & {item: NoteLinkedStructuredTable},
    action: MediocreItemAction
  ): string | null {
    const adapter = getStructuredTableAdapters()[item.item.kind];
    const structuredTable = item.item;
    const computedTable = this.props.computedTables[structuredTable.name] ?? null;
    const primarySamplesByRowIds = adapter.getPrimarySamples(structuredTable, computedTable);
    const liveRowMetadataByRow = (structuredTable as UnknownStructuredTable).rows.map((row) =>
      getLiveRowMetadataFromRowUpdater({
        row,
        primarySamples: primarySamplesByRowIds[row.rowId],
        adapter,
        columns: structuredTable.columns,
      })
    );
    const liveRowUiData = buildRowUiData(structuredTable, liveRowMetadataByRow);
    const tableWithUiData = produce(structuredTable, (oldStructuredTable: UnknownStructuredTable) => {
      oldStructuredTable.rows = liveRowUiData.map((rowUiData: RowDataUiData, rowIndex: number) => ({
        ...oldStructuredTable.rows[rowIndex],
        lastSyncedArchivePurpose: rowUiData.archivePurpose,
      }));
    });
    const allowDelete = adapter.allowDeleteTable(tableWithUiData);
    switch (action) {
      case MediocreItemAction.DELETE:
        if (!allowDelete.canDelete) {
          return allowDelete.reason ?? `Cannot delete "${adapter.getDisplayName(item.item)}" tables.`;
        } else if (!doesUserHavePermissionToViewTable(item.item)) {
          return 'You do not have access to delete this table';
        }
        return null;
      default:
        return null;
    }
  },

  *getSearchResults(
    item: NotePart,
    matcher: TextMatcher,
    searchOverFormulas: boolean,
    computedTables: OutputTableMap | null,
    isCollapsed: boolean
  ): Generator<MediocreSearchResult> {
    const structuredTable = item.item as AnyStructuredTable;
    // Search over the table name
    yield* getSearchResultsForTableName(matcher, structuredTable.name, item.cid);

    if (!isCollapsed) {
      // Search over the table column names
      const columnNames = structuredTable.columns.map((column) => {
        if ((column as BaseTagValueStructuredTableColumn<ClientId>).schemaFieldDisplayName) {
          return (column as BaseTagValueStructuredTableColumn<ClientId>).schemaFieldDisplayName;
        }
        return column.name ?? '';
      });
      yield* getSearchResultsForColumnNames(matcher, columnNames, item.cid);
      const computedTable = computedTables?.[structuredTable.name];
      for (const [i, row] of structuredTable.rows.entries()) {
        for (const [j, cell] of row.cells.entries()) {
          if (
            doesTableCellMatch(matcher, cell, computedTable ? computedTable[i][j] : null, searchOverFormulas)
          ) {
            yield buildTableSearchResult(item.cid, i, j);
          }
        }
      }
    }
  },
};

function notifyAndThrowForExcessiveRows(tableName: string, maxRows: number): never {
  notifications.error(
    <>
      <strong>{tableName}</strong> cannot have more than {maxRows} rows.
    </>
  );
  throw new Error('Attempted to setContent for structured table with more than maxRows rows');
}

export function setStructuredTableContent({
  item,
  table,
  lastSyncedUpstreamValuesByRow,
}: NoteLinkedStructuredTableContent): Spec<NotePart> {
  return {
    item: {
      $apply(existingStructuredTable: NoteLinkedStructuredTable): NoteLinkedStructuredTable {
        assert(
          (Boolean(item) || Boolean(table)) && !(Boolean(item) && Boolean(table)),
          'setContent called without content to set'
        );

        if (table) {
          const structuredTableAdapter = getStructuredTableAdapters()[existingStructuredTable.kind];
          item = structuredTableAdapter.fromTable(table) as NoteLinkedStructuredTable;
        }
        // item is not null at this point but we need to let Typescript know
        assert(item != null, 'Item should not be null at this point');
        const maxRows = getMaxRowsConfig(true, item.kind);
        if (item.rows.length > maxRows && item.rows.length > existingStructuredTable.rows.length) {
          notifyAndThrowForExcessiveRows(item.name, maxRows);
        }

        if (lastSyncedUpstreamValuesByRow != null) {
          assert(
            item.rows.length === lastSyncedUpstreamValuesByRow.length,
            'Synced values should match the length of the table'
          );
        }

        item = autofixStructuredTable(item as UnknownStructuredTable) as NoteLinkedStructuredTable;

        // Remove any UI properties from the cells before persisting the table and add last synced values
        const cleanedRows = item.rows.map((row, rowIndex) => {
          const lastSyncedUpstreamValues =
            lastSyncedUpstreamValuesByRow != null
              ? lastSyncedUpstreamValuesByRow[rowIndex]
              : row.lastSyncedUpstreamValues;

          const cells = row.cells.map((cell) => omit(cell, ...CELL_UI_PROPERTIES));
          return {
            ...row,
            ...(lastSyncedUpstreamValues != null ? {lastSyncedUpstreamValues} : {}),
            cells,
          };
        });
        return {
          ...item,
          rows: cleanedRows,
        } as NoteLinkedStructuredTable;
      },
    },
  };
}

/**
 * Some Table Operations are performed on Plain Tables and Structured Tables, but Structured Table follow
 * stricter rules about how they are formed. These functions fix Structured Tables to be well-formed before
 * saving to the editor.
 */
function autofixStructuredTable(structuredTable: UnknownStructuredTable): UnknownStructuredTable {
  let autofixedStructuredTable = structuredTable;
  // Log a warning for any tables where Single Inputs are not well-formed and fix them
  const areSingleInputsValid = checkStructuredTableSingleInputsValid(structuredTable);
  if (!areSingleInputsValid) {
    autofixedStructuredTable = autofixStructuredTableSingleInputs(autofixedStructuredTable);
  }

  // Fix Parameter Table metadata that may be incorrect due to operations such as removing rows
  if (BenchlingConfig.PROCEDURES_ENABLE) {
    if (autofixedStructuredTable.kind === StructuredTableKind.PARAMETER) {
      autofixedStructuredTable = autofixParameterTableSchemaMetadatas(
        autofixedStructuredTable as ParameterStructuredTable
      );
    }
  }

  return autofixedStructuredTable;
}
